import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import BottomNav from "./components/BottomNav";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Explore from "./pages/Explore";
import CreatePlan from "./pages/CreatePlan";
import PlanDetails from "./pages/PlanDetails";
import Activity from "./pages/Activity";
import Profile from "./pages/Profile";
import HashtagResults from "./pages/HashtagResults";
import Chat from "./pages/Chat";
import Guidelines from "./pages/Guidelines";
import StatsList from "./pages/StatsList";
import EditProfile from "./pages/EditProfile";
import Settings from "./pages/Settings";
import SettingsNotifications from "./pages/SettingsNotifications";
import NewGroupChat from "./pages/NewGroupChat";
import GroupChat from "./pages/GroupChat";
import PlanChatRedirect from "./pages/PlanChatRedirect";
import DM from "./pages/DM";
import VibeDetail from "./pages/VibeDetail";
import StoryViewer from "./pages/StoryViewer";
import StoryEditor from "./pages/StoryEditor";
import StoryCamera from "./pages/StoryCamera";
import SectionFeed from "./pages/SectionFeed";
import AdminReports from "./pages/AdminReports";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminDemoSeed from "./pages/AdminDemoSeed";
import AdminChats from "./pages/AdminChats";
import Leaderboard from "./pages/Leaderboard";
import Levels from "./pages/Levels";
import BetaFeedback from "./pages/BetaFeedback";
import Marketing from "./pages/marketing/Marketing";
import AdminFeedback from "./pages/AdminFeedback";
import AdminVenueInvites from "./pages/AdminVenueInvites";
import AdminWaitlist from "./pages/AdminWaitlist";
import BusinessRegister from "./pages/BusinessRegister";
import BusinessProfile from "./pages/BusinessProfile";
import MyBusinesses from "./pages/MyBusinesses";
import Communities from "./pages/Communities";
import CommunityProfile from "./pages/CommunityProfile";
import CalendarPage from "./pages/CalendarPage";
import Legal from "./pages/Legal";
import PushPermissionDialog from "./components/PushPermissionDialog";
import VerifyBanner from "./components/VerifyBanner";
import { registerPushServiceWorker, attachPushClickHandler } from "./lib/push";
import { useEffect } from "react";

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    // Best-effort: register service worker so push works even before opt-in.
    registerPushServiceWorker();
    const detach = attachPushClickHandler((url) => navigate(url));
    return detach;
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="app-container min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-black tracking-tighter text-[#111111] animate-pulse">asap</h1>
        </div>
      </div>
    );
  }

  if (!user) {
    // Root path shows the marketing site instead of redirecting to /login.
    // Everything else still redirects so protected deep-links stay guarded.
    if (location.pathname === '/') return <Marketing />;
    return <Navigate to="/login" replace />;
  }

  // Hide the bottom nav on conversation-mode pages where a fixed composer
  // owns the bottom of the screen. Otherwise the BottomNav (z-9999) covers
  // the composer and taps fall through — this is what kept breaking the
  // "Reply to a comment" flow on VibeDetail.
  const hideNav =
    location.pathname === '/onboarding' ||
    location.pathname.startsWith('/vibe/');

  return (
    <div className="app-container">
      <VerifyBanner user={user} />
      {/* Spacer so the verify-banner doesn't overlap page top-bars. The
          actual banner is position:fixed; this empty div reserves the
          equivalent height when it's visible. */}
      {user && !user.email_verified && (
        <div aria-hidden="true" className="h-[40px] flex-shrink-0" data-testid="verify-banner-spacer" />
      )}
      <Outlet />
      {!hideNav && <BottomNav />}
      <PushPermissionDialog user={user} />
    </div>
  );
}

function ProtectedRouteNoNav() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="app-container">
      <Outlet />
    </div>
  );
}

function PublicRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Brand-new users without interests go through onboarding first
    const needsOnboarding = !user.interests || user.interests.length === 0;
    return <Navigate to={needsOnboarding ? "/onboarding" : "/"} replace />;
  }
  return (
    <div className="app-container">
      <Outlet />
    </div>
  );
}

/**
 * UniversalRoute — Renders content for everyone (logged in or out).
 * Used for legal pages (TOS/Privacy) which are an App Store requirement
 * to be accessible without authentication.
 */
function UniversalRoute() {
  return (
    <div className="app-container">
      <Outlet />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            {/* Verify email — universal so logged-out + logged-in users can both consume the link */}
            <Route element={<UniversalRoute />}>
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>
            {/* Marketing landing page — bare route (no app-container width lock) */}
            <Route path="/welcome" element={<Marketing />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/" element={<Home />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/create" element={<CreatePlan />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/user/:id" element={<Profile />} />
              <Route path="/plan/:id" element={<PlanDetails />} />
              <Route path="/hashtag/:tag" element={<HashtagResults />} />
              <Route path="/guidelines" element={<Guidelines />} />
              <Route path="/edit-profile" element={<EditProfile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/notifications" element={<SettingsNotifications />} />
              <Route path="/new-group-chat" element={<NewGroupChat />} />
              <Route path="/vibe/:id" element={<VibeDetail />} />
              <Route path="/user/:id/:type" element={<StatsList />} />
              <Route path="/feed/:section" element={<SectionFeed />} />
              <Route path="/admin/reports" element={<AdminReports />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/demo" element={<AdminDemoSeed />} />
              <Route path="/admin/chats" element={<AdminChats />} />
              <Route path="/admin/feedback" element={<AdminFeedback />} />
              <Route path="/admin/venue-invites" element={<AdminVenueInvites />} />
              <Route path="/admin/waitlist" element={<AdminWaitlist />} />
              <Route path="/business/register" element={<BusinessRegister />} />
              <Route path="/business/mine" element={<MyBusinesses />} />
              <Route path="/business/:id" element={<BusinessProfile />} />
              <Route path="/communities" element={<Communities />} />
              <Route path="/community/:id" element={<CommunityProfile />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/levels" element={<Levels />} />
              <Route path="/feedback" element={<BetaFeedback />} />
            </Route>
            {/* Legal pages — accessible to logged-out users (App Store requirement) */}
            <Route element={<UniversalRoute />}>
              <Route path="/terms" element={<Legal />} />
              <Route path="/privacy" element={<Legal />} />
              <Route path="/legal/:kind" element={<Legal />} />
            </Route>
            {/* Chat-like pages — no BottomNav to keep composer pinned to bottom safely.
                Adding new chat surfaces? Place them inside ProtectedRouteNoNav. */}
            <Route element={<ProtectedRouteNoNav />}>
              <Route path="/groups/:id" element={<GroupChat />} />
              <Route path="/plan/:id/chat" element={<PlanChatRedirect />} />
              <Route path="/dm/:id" element={<DM />} />
              <Route path="/stories/:userId" element={<StoryViewer />} />
              <Route path="/story/edit" element={<StoryEditor />} />
              <Route path="/story/camera" element={<StoryCamera />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
